import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { API_URL } from '@/contexts/AuthContext';
import { ehCartao, outrosPagos, pagamentosEstornaveis } from '@/lib/estorno';
import { MOTIVOS_ESTORNO_BELLUNO, descricaoPagamentoBelluno } from '@/lib/belluno';
import { formatBRL } from '@/lib/format';

const comMaiuscula = (texto) => texto.charAt(0).toUpperCase() + texto.slice(1);

/**
 * Estorno do pagamento (perfil administrativo): cartão via Pagar.me ou um link da Belluno (cartão ou PIX), o do
 * pedido inteiro ou o de uma parte do pagamento dividido. Com mais de um pagamento estornável, escolhe-se qual.
 * Total cancela o pedido no ERP quando nenhum outro pagamento segue pago; parcial só registra o valor.
 */
export default function EstornoDialog({ order, open, onOpenChange, onDone }) {
  const opcoes = pagamentosEstornaveis(order);
  const [chave, setChave] = useState(null);
  const alvo = opcoes.find((o) => o.chave === chave) || opcoes[0] || null;
  const belluno = Boolean(alvo?.belluno);
  const restante = alvo?.restante || 0;
  const outros = alvo ? outrosPagos(order, alvo) : [];
  const partes = order?.pagamento_split || [];
  const jaNoTiny = (order?.tiny_pedidos || []).some((t) => t.numero_tiny);
  const doisCartoes = !belluno && partes.filter(ehCartao).length > 1;

  const [tipo, setTipo] = useState('total');
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [razao, setRazao] = useState(String(MOTIVOS_ESTORNO_BELLUNO[0].value)); // código que a Belluno exige
  const [enviando, setEnviando] = useState(false);

  // Cada abertura começa limpa (o diálogo é um só, o pedido muda).
  useEffect(() => {
    if (open) {
      setChave(null);
      setTipo('total');
      setValor('');
      setMotivo('');
      setRazao(String(MOTIVOS_ESTORNO_BELLUNO[0].value));
    }
  }, [open, order?.id]);

  const total = tipo === 'total';
  const valorNum = total ? restante : Number(String(valor).replace(',', '.'));
  const valorOk = total || (valorNum > 0 && valorNum <= restante + 0.005);
  const podeEnviar = Boolean(alvo) && valorOk && motivo.trim().length > 0 && !enviando;

  const enviar = async () => {
    setEnviando(true);
    try {
      const body = { motivo: motivo.trim(), valor: total ? null : Number(valorNum.toFixed(2)) };
      if (belluno) body.razao = Number(razao);
      if (alvo.parte != null) body.parte = alvo.parte;
      const { data } = await axios.post(`${API_URL}/pedidos/${order.id}/${belluno ? 'belluno/estornar' : 'estornar'}`, body);
      toast.success(
        data.total
          ? `Estorno de ${formatBRL(data.valor)} realizado. Pedido cancelado.`
          : `Estorno ${total ? '' : 'parcial '}de ${formatBRL(data.valor)} realizado`,
      );
      onDone?.(data);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Não foi possível estornar');
      // 502 = a Pagar.me recusou parte: o que saiu já está gravado no pedido (a Belluno recusa tudo ou nada).
      if (err.response?.status === 502 && !belluno) onDone?.();
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !enviando && onOpenChange(v)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-4 w-4 text-rose-500" />
            Estornar pagamento
          </DialogTitle>
          <DialogDescription>
            Pedido {order?.numero_pedido} · {order?.cliente_nome} · {formatBRL(restante)} pago
            {alvo?.parte != null && ` no pagamento ${alvo.parte}`}
            {belluno ? ` pela Belluno (${descricaoPagamentoBelluno(alvo.link)})` : ' no cartão'}
            {Number(order?.valor_estornado) > 0 && ` (já estornado no pedido ${formatBRL(order.valor_estornado)})`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {opcoes.length > 1 && (
            <div className="space-y-1.5">
              <Label htmlFor="estorno-pagamento">Pagamento a estornar</Label>
              <select
                id="estorno-pagamento"
                value={alvo?.chave}
                onChange={(e) => { setChave(e.target.value); setValor(''); }}
                className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring/20"
                data-testid="estorno-pagamento"
              >
                {opcoes.map((o) => <option key={o.chave} value={o.chave}>{comMaiuscula(o.rotulo)}: {formatBRL(o.restante)}</option>)}
              </select>
            </div>
          )}

          <RadioGroup value={tipo} onValueChange={setTipo} className="gap-2">
            <label className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2 cursor-pointer has-[[data-state=checked]]:border-rose-400/60">
              <RadioGroupItem value="total" id="estorno-total" />
              <span className="text-sm">
                <span className="font-medium text-slate-800 dark:text-slate-100">Total</span>
                <span className="text-slate-500 dark:text-slate-400">
                  {': '}{formatBRL(restante)}{outros.length ? `; segue pago: ${outros.join(', ')}` : ', cancela o pedido'}
                </span>
              </span>
            </label>
            <label className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2 cursor-pointer has-[[data-state=checked]]:border-rose-400/60">
              <RadioGroupItem value="parcial" id="estorno-parcial" />
              <span className="text-sm">
                <span className="font-medium text-slate-800 dark:text-slate-100">Parcial</span>
                <span className="text-slate-500 dark:text-slate-400">: o pedido continua como está</span>
              </span>
            </label>
          </RadioGroup>

          {!total && (
            <div className="space-y-1.5">
              <Label htmlFor="estorno-valor">Valor a estornar (máx. {formatBRL(restante)})</Label>
              <Input
                id="estorno-valor"
                type="number"
                inputMode="decimal"
                min="0.01"
                max={restante}
                step="0.01"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="0,00"
                autoFocus
              />
              {valor !== '' && !valorOk && (
                <p className="text-xs text-rose-600 dark:text-rose-400">Informe um valor entre R$ 0,01 e {formatBRL(restante)}.</p>
              )}
            </div>
          )}

          {belluno && (
            <div className="space-y-1.5">
              <Label htmlFor="estorno-razao">Motivo para a Belluno *</Label>
              <select
                id="estorno-razao"
                value={razao}
                onChange={(e) => setRazao(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring/20"
                data-testid="estorno-razao"
              >
                {MOTIVOS_ESTORNO_BELLUNO.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="estorno-motivo">{belluno ? 'Observação *' : 'Motivo *'}</Label>
            <Textarea
              id="estorno-motivo"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex: cliente desistiu, produto indisponível, cobrança em duplicidade..."
            />
          </div>

          <ul className="space-y-1 text-xs text-amber-700 dark:text-amber-300/90">
            <li className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
              {belluno && alvo.link?.metodo === 'pix'
                ? 'O valor volta por PIX para a conta de origem, no prazo da Belluno. Não dá para desfazer.'
                : 'O valor volta ao cartão do cliente em até 2 faturas (prazo da bandeira). Não dá para desfazer.'}
            </li>
            {jaNoTiny && (
              <li className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />Este pedido já foi ao Tiny: o cancelamento lá é manual.</li>
            )}
            {doisCartoes && !total && (
              <li className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />Em 2 cartões, o parcial sai do primeiro cartão até esgotar, depois do segundo.</li>
            )}
          </ul>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={enviando}>Cancelar</Button>
          <Button variant="destructive" onClick={enviar} disabled={!podeEnviar} data-testid="confirmar-estorno">
            {enviando ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Undo2 className="h-4 w-4 mr-1.5" />}
            {enviando ? 'Estornando...' : `Estornar ${valorOk ? formatBRL(valorNum) : ''}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
